import React, { useMemo } from 'react';
import { InboxOutlined, FileTextOutlined, BranchesOutlined, ShopOutlined } from '@ant-design/icons';
import './DefinitionCategoryTabs.css';

/** Gmail 風格分類標籤色（依序循環，暫不從 master data 讀取） */
export const DEFINITION_TAB_COLOR_PALETTE = [
  { accent: '#137333', badgeBg: '#137333', badgeText: '#ffffff' },
  { accent: '#1967d2', badgeBg: '#1967d2', badgeText: '#ffffff' },
  { accent: '#d93025', badgeBg: '#e37400', badgeText: '#ffffff' },
  { accent: '#b06000', badgeBg: '#f9ab00', badgeText: '#202124' },
  { accent: '#7627bb', badgeBg: '#7627bb', badgeText: '#ffffff' },
  { accent: '#00796b', badgeBg: '#00796b', badgeText: '#ffffff' },
  { accent: '#c5221f', badgeBg: '#c5221f', badgeText: '#ffffff' },
  { accent: '#3c4043', badgeBg: '#5f6368', badgeText: '#ffffff' },
];

const ALL_TAB_STYLE = {
  accent: '#1a73e8',
  badgeBg: '#e8f0fe',
  badgeText: '#1967d2',
};

const ICON_BY_TYPE = {
  form: FileTextOutlined,
  workflow: BranchesOutlined,
  company: ShopOutlined,
};

/**
 * @param {Object} props
 * @param {Array<{ id: string|number|null, name: string, count?: number, subtitle?: string }>} props.items
 * @param {string|number|null} props.value - 目前選中 id；null 或 allValue 表示「全部」
 * @param {string|number|null} [props.allValue=null]
 * @param {function} props.onChange
 * @param {string} props.allLabel
 * @param {number} [props.allCount]
 * @param {string} [props.allSubtitle]
 * @param {'form'|'workflow'|'company'} [props.iconType='form']
 * @param {boolean} [props.loading=false]
 */
const DefinitionCategoryTabs = ({
  items = [],
  value,
  allValue = null,
  onChange,
  allLabel,
  allCount = 0,
  allSubtitle = '',
  iconType = 'form',
  loading = false,
}) => {
  const TabIcon = ICON_BY_TYPE[iconType] || FileTextOutlined;
  const isAllSelected = value === allValue || value == null || value === 'all';

  const tabs = useMemo(() => {
    const allTab = {
      id: allValue,
      name: allLabel,
      count: allCount,
      subtitle: allSubtitle,
      color: ALL_TAB_STYLE,
      isAll: true,
    };
    const defTabs = items.map((item, index) => ({
      ...item,
      color: DEFINITION_TAB_COLOR_PALETTE[index % DEFINITION_TAB_COLOR_PALETTE.length],
      isAll: false,
    }));
    return [allTab, ...defTabs];
  }, [items, allLabel, allCount, allSubtitle, allValue]);

  const formatCount = (count) => {
    const n = Number(count) || 0;
    if (n <= 0) return '0';
    return n > 999 ? '999+' : String(n);
  };

  return (
    <div
      className={`definition-category-tabs${loading ? ' definition-category-tabs--loading' : ''}`}
      role="tablist"
    >
      {tabs.map((tab) => {
        const selected = tab.isAll
          ? isAllSelected
          : String(value) === String(tab.id);
        const Icon = tab.isAll ? InboxOutlined : TabIcon;
        const count = tab.count ?? 0;
        const badgeLabel = count > 0 ? `${formatCount(count)}` : '0';

        return (
          <button
            key={tab.isAll ? '__all__' : String(tab.id)}
            type="button"
            role="tab"
            aria-selected={selected}
            className={`definition-category-tab${selected ? ' is-active' : ''}`}
            onClick={() => onChange(tab.isAll ? allValue : tab.id)}
            title={tab.name}
          >
            <div className="definition-category-tab__head">
              <Icon
                className="definition-category-tab__icon"
                style={{ color: selected ? tab.color.accent : '#5f6368' }}
              />
              <span className="definition-category-tab__title">{tab.name}</span>
              <span
                className={`definition-category-tab__badge${count === 0 ? ' is-zero' : ''}`}
                style={{
                  backgroundColor: tab.isAll ? tab.color.badgeBg : tab.color.badgeBg,
                  color: tab.color.badgeText,
                }}
              >
                {badgeLabel}
              </span>
            </div>
            {tab.subtitle ? (
              <div className="definition-category-tab__sub">{tab.subtitle}</div>
            ) : null}
          </button>
        );
      })}
    </div>
  );
};

export default DefinitionCategoryTabs;
